import { Linkedin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import footerLogo from "@/assets/footer-logo.png";
import { CookiePreferencesButton } from "@/components/CookieConsent";
import { useLanguage } from "@/i18n/LanguageContext";

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/ganiga", label: "LinkedIn" },
  { icon: Instagram, href: "https://www.instagram.com/ganiga.ai?igsh=MTVkYjJsazkxeWcx", label: "Instagram" },
];

export const Footer = () => {
  const { t, getLocalizedPath } = useLanguage();

  const footerLinks = {
    prodotti: [
      { label: "HooolyIndoor", href: "#products" },
      { label: "HooolySmall", href: "#products" },
      { label: "HooolyFood", href: "#products" },
      { label: "Hoooly Dashboard", href: "#products" },
    ],
    azienda: [
      { label: t.footer.aboutUs, href: "#" },
      { label: t.footer.caseStudy, href: "#partners" },
      {
        label: t.footer.careers,
        href: "https://industrious-rise-3cb.notion.site/20cad284a2434e70bf2402a372808020?pvs=105",
        external: true,
      },
      { label: t.footer.contact, href: "/contact", isRoute: true },
    ],
    legal: [
      { label: t.footer.privacyPolicy, href: "/privacy-policy", isRoute: true },
      { label: t.footer.cookiePolicy, href: "/cookie-policy", isRoute: true },
    ],
  };

  return (
    <footer className="bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-2 space-y-8">
            <div className="inline-block bg-background rounded-2xl p-5">
              <img
                src={footerLogo}
                alt="Ganiga - NVIDIA Inception Program & Google for Startups partner"
                className="h-24 w-auto"
                loading="lazy"
              />
            </div>
            <p className="text-background/60 font-light max-w-sm leading-relaxed text-lg">
              {t.footer.description}
            </p>

            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-background/10 flex items-center justify-center hover:bg-primary hover:scale-105 transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Prodotti */}
          <div>
            <h2 className="font-bold mb-6 text-lg">{t.footer.products}</h2>
            <ul className="space-y-4">
              {footerLinks.prodotti.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-background/60 hover:text-background transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Soluzioni - SEO keyword links */}
          <div>
            <h2 className="font-bold mb-6 text-lg">{t.footer.solutions}</h2>
            <ul className="space-y-4">
              <li>
                <a href="#benefits" className="text-background/60 hover:text-background transition-colors">
                  {t.footer.solutionLinks.tariReduction}
                </a>
              </li>
              <li>
                <a href="#benefits" className="text-background/60 hover:text-background transition-colors">
                  {t.footer.solutionLinks.esgReporting}
                </a>
              </li>
              <li>
                <Link to={getLocalizedPath("/rental")} className="text-background/60 hover:text-background transition-colors">
                  {t.footer.solutionLinks.eventRental}
                </Link>
              </li>
              <li>
                <a href="#benefits" className="text-background/60 hover:text-background transition-colors">
                  {t.footer.solutionLinks.operationalCosts}
                </a>
              </li>
            </ul>
          </div>

          {/* Azienda */}
          <div>
            <h2 className="font-bold mb-6 text-lg">{t.footer.company}</h2>
            <ul className="space-y-4">
              {footerLinks.azienda.map((link) => (
                <li key={link.label}>
                  {link.isRoute ? (
                    <Link
                      to={getLocalizedPath(link.href)}
                      className="text-background/60 hover:text-background transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-background/60 hover:text-background transition-colors"
                      {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-bold mb-6 text-lg">{t.footer.legal}</h2>
            <ul className="space-y-4">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  {link.isRoute ? (
                    <Link
                      to={getLocalizedPath(link.href)}
                      className="text-background/60 hover:text-background transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-background/60 hover:text-background transition-colors">
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
              <li>
                <CookiePreferencesButton />
              </li>
            </ul>
          </div>
        </div>

        {/* Company Legal Info */}
        <div className="mt-16 pt-10 border-t border-background/10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 text-sm text-background/50">
            <div>
              <p className="font-medium text-background/70 mb-1">{t.footer.companyName}</p>
              <p>GANIGA INNOVATION S.R.L.</p>
            </div>
            <div>
              <p className="font-medium text-background/70 mb-1">{t.footer.headquarters}</p>
              <p>Via Valdinievole Nord 66, 56031 Bientina (PI)</p>
            </div>
            <div>
              <p className="font-medium text-background/70 mb-1">{t.footer.vatNumber}</p>
              <p>02420420503 | REA: PI-205751</p>
            </div>
          </div>
          <p className="mt-6 text-sm text-background/60">
            PEC: ganigainnovationsrls@cenaspec.it
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-8 border-t border-background/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-background/60">
            {t.footer.copyright.replace("{year}", new Date().getFullYear().toString())}
          </p>
          <p className="text-sm text-background/60">{t.footer.madeWith}</p>
        </div>
      </div>
    </footer>
  );
};
